import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation, RefreshCw } from 'lucide-react';

// Leaflet will be loaded dynamically
let L = null;

const LiveTrackingMap = ({
  driverLocation,
  pickupLocation,
  dropLocation,
  locationHistory = [],
  isTracking = false,
  showRoute = true,
  height = '400px',
  onMapReady,
  className = ''
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropMarkerRef = useRef(null);
  const routeLineRef = useRef(null);
  const historyLineRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [containerReady, setContainerReady] = useState(false);
  const [error, setError] = useState(null);

  // Callback ref to detect when map container is mounted
  const mapContainerRef = (node) => {
    mapRef.current = node;
    if (node) {
      setContainerReady(true);
    }
  };

  // Load Leaflet dynamically
  useEffect(() => {
    const loadLeaflet = async () => {
      try {
        // Check if Leaflet is already loaded
        if (window.L) {
          L = window.L;
          setMapLoaded(true);
          return;
        }

        // Load Leaflet CSS
        if (!document.getElementById('leaflet-css')) {
          const link = document.createElement('link');
          link.id = 'leaflet-css';
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
          link.crossOrigin = '';
          document.head.appendChild(link);
        }

        // Load Leaflet JS
        const existingScript = document.getElementById('leaflet-js');
        if (existingScript) {
          // Script tag exists but window.L not ready yet — wait for it
          if (!window.L) {
            await new Promise((resolve) => {
              existingScript.addEventListener('load', resolve);
              // In case it already loaded between our check and listener
              if (window.L) resolve();
              // Fallback timeout
              setTimeout(resolve, 3000);
            });
          }
        } else {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.id = 'leaflet-js';
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
            script.crossOrigin = '';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        L = window.L;
        setMapLoaded(true);
      } catch (err) {
        console.error('Error loading Leaflet:', err);
        setError('Failed to load map library');
      }
    };

    loadLeaflet();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapLoaded || !containerReady || !mapRef.current) return;
    // Already initialized
    if (mapInstanceRef.current) return;

    // Ensure L is set from window
    if (!L && window.L) {
      L = window.L;
    }
    if (!L) return;

    // Small delay to ensure DOM container has proper dimensions
    const initTimer = setTimeout(() => {
      if (mapInstanceRef.current) return; // double-check

      try {
        // Default center (India)
        const defaultCenter = [20.5937, 78.9629];
        const initialCenter = driverLocation
          ? [driverLocation.latitude, driverLocation.longitude]
          : pickupLocation?.latitude
          ? [pickupLocation.latitude, pickupLocation.longitude]
          : defaultCenter;

        // Create map instance
        const map = L.map(mapRef.current, {
          center: initialCenter,
          zoom: 13,
          zoomControl: true,
          scrollWheelZoom: true
        });

        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19
        }).addTo(map);

        mapInstanceRef.current = map;

        // Invalidate size after a tick to handle container sizing
        setTimeout(() => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize();
          }
        }, 100);

        if (onMapReady) {
          onMapReady(map);
        }
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to initialize map');
      }
    }, 50);

    return () => {
      clearTimeout(initTimer);
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.stop();
          mapInstanceRef.current.off();
          mapInstanceRef.current.remove();
        } catch (e) {}
        mapInstanceRef.current = null;
        driverMarkerRef.current = null;
        pickupMarkerRef.current = null;
        dropMarkerRef.current = null;
        routeLineRef.current = null;
        historyLineRef.current = null;
      }
    };
  }, [mapLoaded, containerReady]);

  // Create custom icons
  const createIcons = () => {
    if (!L && window.L) L = window.L;
    if (!L) return {};

    // Car/Driver icon
    const driverIcon = L.divIcon({
      className: 'custom-driver-marker',
      html: `
        <div style="
          background: #3B82F6;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10c-.5-1.5-2-2.5-3.5-2.5h-5C8 7.5 6.5 8.5 6 10l-2.5 1.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"/>
            <circle cx="7" cy="17" r="2"/>
            <circle cx="17" cy="17" r="2"/>
          </svg>
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    });

    // Pickup icon (green)
    const pickupIcon = L.divIcon({
      className: 'custom-pickup-marker',
      html: `
        <div style="
          background: #10B981;
          width: 32px;
          height: 32px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        ">
          <span style="transform: rotate(45deg); color: white; font-weight: bold; font-size: 14px;">P</span>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 32]
    });

    // Drop icon (red)
    const dropIcon = L.divIcon({
      className: 'custom-drop-marker',
      html: `
        <div style="
          background: #EF4444;
          width: 32px;
          height: 32px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        ">
          <span style="transform: rotate(45deg); color: white; font-weight: bold; font-size: 14px;">D</span>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 32]
    });

    return { driverIcon, pickupIcon, dropIcon };
  };

  // Update driver marker
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !driverLocation) return;

    const { driverIcon } = createIcons();
    const position = [driverLocation.latitude, driverLocation.longitude];

    if (driverMarkerRef.current) {
      // Update existing marker position with animation
      driverMarkerRef.current.setLatLng(position);
      // Pan to driver
      mapInstanceRef.current.panTo(position, { animate: true, duration: 0.5 });
    } else {
      // First time showing driver — create marker and zoom in
      driverMarkerRef.current = L.marker(position, { icon: driverIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup('Driver Location');
      // Zoom in to driver location (first appearance)
      mapInstanceRef.current.setView(position, 15, { animate: true });
    }
  }, [driverLocation, mapLoaded]);

  // Update pickup marker
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !pickupLocation?.latitude) return;

    const { pickupIcon } = createIcons();
    const position = [pickupLocation.latitude, pickupLocation.longitude];

    if (pickupMarkerRef.current) {
      pickupMarkerRef.current.setLatLng(position);
    } else {
      pickupMarkerRef.current = L.marker(position, { icon: pickupIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(pickupLocation.address || 'Pickup Location');
    }
  }, [pickupLocation, mapLoaded]);

  // Update drop marker
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !dropLocation?.latitude) return;

    const { dropIcon } = createIcons();
    const position = [dropLocation.latitude, dropLocation.longitude];

    if (dropMarkerRef.current) {
      dropMarkerRef.current.setLatLng(position);
    } else {
      dropMarkerRef.current = L.marker(position, { icon: dropIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(dropLocation.address || 'Drop Location');
    }
  }, [dropLocation, mapLoaded]);

  // Draw route line between pickup and drop
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !showRoute) return;
    if (!pickupLocation?.latitude || !dropLocation?.latitude) return;

    // Remove existing route line
    if (routeLineRef.current) {
      mapInstanceRef.current.removeLayer(routeLineRef.current);
    }

    // Draw dashed line between pickup and drop
    const routeCoords = [
      [pickupLocation.latitude, pickupLocation.longitude],
      [dropLocation.latitude, dropLocation.longitude]
    ];

    routeLineRef.current = L.polyline(routeCoords, {
      color: '#6B7280',
      weight: 3,
      dashArray: '10, 10',
      opacity: 0.6
    }).addTo(mapInstanceRef.current);

    // Fit bounds to show all markers
    const bounds = L.latLngBounds(routeCoords);
    if (driverLocation) {
      bounds.extend([driverLocation.latitude, driverLocation.longitude]);
    }
    mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
  }, [pickupLocation, dropLocation, showRoute, mapLoaded]);

  // Draw location history trail
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || locationHistory.length < 2) return;

    // Remove existing history line
    if (historyLineRef.current) {
      mapInstanceRef.current.removeLayer(historyLineRef.current);
    }

    // Create coordinates array from history
    const historyCoords = locationHistory.map(loc => [loc.latitude, loc.longitude]);

    // Draw trail line
    historyLineRef.current = L.polyline(historyCoords, {
      color: '#3B82F6',
      weight: 4,
      opacity: 0.7
    }).addTo(mapInstanceRef.current);
  }, [locationHistory, mapLoaded]);

  // Center map on driver location
  const centerOnDriver = () => {
    if (mapInstanceRef.current && driverLocation) {
      mapInstanceRef.current.setView(
        [driverLocation.latitude, driverLocation.longitude],
        15,
        { animate: true }
      );
    }
  };

  // Fit all markers in view
  const fitAllMarkers = () => {
    if (!mapInstanceRef.current) return;

    const bounds = [];
    if (driverLocation) {
      bounds.push([driverLocation.latitude, driverLocation.longitude]);
    }
    if (pickupLocation?.latitude) {
      bounds.push([pickupLocation.latitude, pickupLocation.longitude]);
    }
    if (dropLocation?.latitude) {
      bounds.push([dropLocation.latitude, dropLocation.longitude]);
    }

    if (bounds.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center text-gray-500">
          <MapPin className="h-10 w-10 mx-auto mb-2 text-gray-400" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ height }}>
      {/* Map Container */}
      <div
        ref={mapContainerRef}
        className="w-full h-full rounded-lg overflow-hidden"
        style={{ minHeight: '200px' }}
      />

      {/* Loading overlay */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
            <p className="text-gray-600 text-sm">Loading map...</p>
          </div>
        </div>
      )}

      {/* Map controls */}
      {mapLoaded && (
        <div className="absolute bottom-4 right-4 flex flex-col gap-2 z-[1000]">
          {driverLocation && (
            <button
              onClick={centerOnDriver}
              className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
              title="Center on driver"
            >
              <Navigation className="h-5 w-5 text-blue-600" />
            </button>
          )}
          <button
            onClick={fitAllMarkers}
            className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
            title="Fit all markers"
          >
            <MapPin className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      )}

      {/* Tracking indicator */}
      {isTracking && (
        <div className="absolute top-4 left-4 z-[1000]">
          <div className="flex items-center bg-green-500 text-white px-3 py-1.5 rounded-full text-sm shadow-md">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse mr-2" />
            Live Tracking
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTrackingMap;
